import { Banknote, CreditCard, Gift, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { MoneyText } from "@/components/MoneyText";
import type { CartCustomer, PaymentMethods, TenderPreview } from "@/lib/types";

import { EdcForm } from "./EdcForm";
import type { PartMethod, TenderPart } from "./useTender";

const ICON = { cash: Banknote, edc_external: CreditCard, store_credit: Gift } as const;
const LABEL = {
  cash: "tender.method_cash",
  edc_external: "tender.method_card",
  store_credit: "tender.method_credit",
} as const;

/**
 * 分割 (design/05 §3.2): several ways of paying for one receipt (cash + card + store credit). A new
 * part starts with the server's 残額; when 残額 is ¥0 the 「¥23,500 を受け取る」 button opens.
 */
export function SplitList({
  parts,
  preview,
  methods,
  customer,
  onAdd,
  onChange,
  onRemove,
  onChooseCustomer,
}: {
  parts: TenderPart[];
  preview: TenderPreview | undefined;
  methods: PaymentMethods | undefined;
  customer: CartCustomer | null;
  onAdd: (method: PartMethod) => void;
  onChange: (id: string, changes: Partial<TenderPart>) => void;
  onRemove: (id: string) => void;
  onChooseCustomer: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-3">
      <ul className="flex flex-col gap-3">
        {parts.map((p, index) => {
          const Icon = ICON[p.method];
          const line = preview?.tenders.find((l) => l.index === index);
          return (
            <li
              key={p.id}
              data-part={p.method}
              className="flex flex-col gap-3 rounded-lg border p-3"
            >
              <div className="flex items-center gap-3">
                <Icon aria-hidden className="size-6" />
                <span className="text-lg font-semibold">{t(LABEL[p.method])}</span>
                <label className="ml-auto flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">{t("tender.split_amount")}</span>
                  <input
                    value={p.amount}
                    onChange={(e) =>
                      onChange(p.id, { amount: e.target.value.replace(/\D/g, "").slice(0, 9) })
                    }
                    inputMode="numeric"
                    aria-label={`${t(LABEL[p.method])} ${t("tender.split_amount")}`}
                    className="min-h-pos-touch w-40 rounded-md border px-3 text-right text-2xl font-semibold tabular-nums"
                  />
                </label>
                <button
                  type="button"
                  onClick={() => onRemove(p.id)}
                  aria-label={t("tender.split_remove")}
                  className="flex size-pos-touch items-center justify-center rounded-md border"
                >
                  <X aria-hidden />
                </button>
              </div>
              {p.method === "cash" ? (
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">{t("tender.tendered")}</span>
                    <input
                      value={p.tendered}
                      placeholder={p.amount}
                      onChange={(e) =>
                        onChange(p.id, { tendered: e.target.value.replace(/\D/g, "").slice(0, 9) })
                      }
                      inputMode="numeric"
                      aria-label={t("tender.tendered")}
                      className="min-h-12 w-40 rounded-md border px-3 text-right text-xl tabular-nums"
                    />
                  </label>
                  {line && (line.shortBy ?? 0) > 0 ? (
                    <span className="text-pos-problem">{t("tender.not_enough")}</span>
                  ) : line && (line.change ?? 0) > 0 ? (
                    <span>
                      {t("tender.change")} <MoneyText amount={line.change ?? 0} />
                    </span>
                  ) : null}
                </div>
              ) : null}
              {p.method === "edc_external" ? (
                <EdcForm
                  part={p}
                  index={index}
                  methods={methods}
                  preview={preview}
                  onChange={(c) => onChange(p.id, c)}
                />
              ) : null}
              {p.method === "store_credit" && !customer ? (
                <button
                  type="button"
                  onClick={onChooseCustomer}
                  className="min-h-12 self-start rounded-md border px-4"
                >
                  {t("tender.credit_choose_customer")}
                </button>
              ) : null}
            </li>
          );
        })}
      </ul>
      <div className="grid grid-cols-3 gap-2">
        <button
          type="button"
          onClick={() => onAdd("cash")}
          className="min-h-pos-touch rounded-md border border-dashed text-lg"
        >
          {t("tender.split_add_cash")}
        </button>
        <button
          type="button"
          onClick={() => onAdd("edc_external")}
          className="min-h-pos-touch rounded-md border border-dashed text-lg"
        >
          {t("tender.split_add_card")}
        </button>
        <button
          type="button"
          onClick={() => onAdd("store_credit")}
          className="min-h-pos-touch rounded-md border border-dashed text-lg"
        >
          {t("tender.split_add_credit")}
        </button>
      </div>
    </div>
  );
}
